import * as React from "react"
import { Check, ChevronsUpDown, PlusCircle, LogOut, Settings, BookOpen, Laptop, Moon, Sun } from "lucide-react"
import { useTheme } from "next-themes"

import { cn } from "@/lib/utils"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu"
import { Dialog } from "@/components/ui/dialog"
import { useNavigate, useLocation } from "react-router-dom"
import { ROUTES } from "@/routes/path"
import { useSwitchOrganization, useActiveOrganization, useSetActiveOrganization } from "@/hooks/useOrganization"
import { useSelector } from "react-redux"
import { RootState } from "@/lib/store"
import { logout } from "@/lib/http/auth"
import { useAuth } from "@/hooks/useAuth"

type OrganizationSwitcherProps = {
  className?: string
  isCollapsed?: boolean
}

export default function OrganizationSwitcher({ className, isCollapsed = false }: OrganizationSwitcherProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const [mainMenuOpen, setMainMenuOpen] = React.useState(false)
  const [showNewOrgDialog, setShowNewOrgDialog] = React.useState(false)
  const [mounted, setMounted] = React.useState(false)

  const { organizations } = useSelector((state: RootState) => state.organization)
  const activeOrganization = useActiveOrganization()
  const setActiveOrganization = useSetActiveOrganization()
  const { user } = useAuth()
  const { theme, setTheme } = useTheme()

  React.useEffect(() => {
    setMounted(true)
  }, [])

  const switchOrganizationMutation = useSwitchOrganization()

  const handleOrgSelect = (org: any) => {
    switchOrganizationMutation.mutate(org.id, {
      onSuccess: () => {
        setActiveOrganization(org)
        setMainMenuOpen(false)

        // Check if current URL contains a thread ID and redirect to studio index
        if (location.pathname.includes("/chat/") && location.pathname !== ROUTES.DASHBOARD.STUDIO.INDEX) {
          window.location.href = ROUTES.DASHBOARD.STUDIO.INDEX
          return
        }

        window.location.reload()
      },
    })
  }

  const handleSettingsClick = () => {
    setMainMenuOpen(false)
    navigate(ROUTES.DASHBOARD.SETTINGS.INDEX)
  }

  const handleDocumentationClick = () => {
    setMainMenuOpen(false)
    window.open("https://docs.ledgerbeam.com", "_blank")
  }

  const handleLogoutClick = () => {
    setMainMenuOpen(false)
    logout()
  }

  const personalAccount = {
    id: "personal",
    name: "Personal Account",
  }

  const orgs = organizations.length > 0 ? organizations : [personalAccount]
  const userEmail = user?.email || ""

  return (
    <Dialog open={showNewOrgDialog} onOpenChange={setShowNewOrgDialog}>
      <DropdownMenu open={mainMenuOpen} onOpenChange={setMainMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className={cn("mx-4 mb-5 justify-between", isCollapsed ? "h-8 w-8 p-0" : "", className)}
          >
            <Avatar className={cn("h-5 w-fit", isCollapsed ? "mx-auto mr-0" : "mr-2")}>
              <AvatarImage
                src={`https://avatar.vercel.sh/${activeOrganization?.id}.png`}
                alt={activeOrganization?.name}
              />
              <AvatarFallback>{activeOrganization?.name?.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            {!isCollapsed && (
              <>
                {activeOrganization?.name}
                <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-60">
          {/* User Email */}
          {userEmail && (
            <>
              <div className="px-2 py-1.5 text-sm text-gray-600 dark:text-gray-400">{userEmail}</div>
              <DropdownMenuSeparator />
            </>
          )}

          {/* Switch Organization - nested submenu */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <ChevronsUpDown className="mr-2 h-4 w-4" />
              Switch Organization
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-60 p-0">
              <Command>
                <CommandList>
                  <CommandInput placeholder="Search organization..." />
                  <CommandEmpty>No organization found.</CommandEmpty>
                  <CommandGroup heading={"Organizations"}>
                    {orgs.map((org: any) => (
                      <CommandItem key={org.id} onSelect={() => handleOrgSelect(org)} className="text-sm">
                        <Avatar className="mr-2 h-5 w-5">
                          <AvatarImage
                            src={`https://avatar.vercel.sh/${org.id}.png`}
                            alt={org.name}
                            className="grayscale"
                          />
                          <AvatarFallback>{org.name?.charAt(0).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        {org.name}
                        <Check
                          className={cn(
                            "ml-auto h-4 w-4",
                            activeOrganization?.id === org.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
                <CommandSeparator />
                <CommandList>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setMainMenuOpen(false)
                        navigate(ROUTES.DASHBOARD.ORGANIZATION.CREATE)
                      }}
                      className="cursor-pointer"
                    >
                      <PlusCircle className="mr-2 h-5 w-5" />
                      Create Organization
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {/* Settings */}
          <DropdownMenuItem onSelect={handleSettingsClick} className="cursor-pointer py-2">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </DropdownMenuItem>

          {/* Theme Toggle */}
          {mounted && (
            <div className="px-2 py-0.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sun className="mr-2 h-4 w-4 text-gray-600 dark:text-gray-300" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Theme</span>
                </div>
                <div className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setTheme("system")}
                    className={cn(
                      "h-7 w-7 rounded-md p-0",
                      theme === "system"
                        ? "bg-white shadow-sm dark:bg-gray-700"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700/50"
                    )}
                    aria-label="System theme"
                  >
                    <Laptop className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setTheme("light")}
                    className={cn(
                      "h-7 w-7 rounded-md p-0",
                      theme === "light"
                        ? "bg-white shadow-sm dark:bg-gray-700"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700/50"
                    )}
                    aria-label="Light theme"
                  >
                    <Sun className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setTheme("dark")}
                    className={cn(
                      "h-7 w-7 rounded-md p-0",
                      theme === "dark"
                        ? "bg-white shadow-sm dark:bg-gray-700"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700/50"
                    )}
                    aria-label="Dark theme"
                  >
                    <Moon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Documentation */}
          <DropdownMenuItem onSelect={handleDocumentationClick} className="cursor-pointer py-2">
            <BookOpen className="mr-2 h-4 w-4" />
            Documentation
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Logout */}
          <DropdownMenuItem
            onSelect={handleLogoutClick}
            className="cursor-pointer py-2 text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </Dialog>
  )
}
